import { Container, Sprite, Texture } from "pixi.js";

const EASE_STOPS = 70;

/**
 * La forme du voile : `steepness` plus bas, il prend plus vite sous la ligne ;
 * `opaqueFrom`, la part de la bande au-delà de laquelle il est opaque.
 */
export type PastOverlayCurve = { steepness: number; opaqueFrom: number };

/** Le voile d'origine : les notes passées s'y enfoncent doucement. */
export const SOFT_CURVE: PastOverlayCurve = { steepness: 0.4, opaqueFrom: 1 };

/** Avec les paillettes : la note est déjà partie, le voile prend tout de suite. */
export const STEEP_CURVE: PastOverlayCurve = { steepness: 0.25, opaqueFrom: 0.75 };

function easedDarken(t: number, curve: PastOverlayCurve): number {
  const reach = Math.min(t / curve.opaqueFrom, 1);

  return 1 - Math.cos((Math.pow(reach, curve.steepness) * Math.PI) / 2);
}

function applyEasedGradient(ctx: CanvasRenderingContext2D, width: number, height: number, curve: PastOverlayCurve): void {
  const gradient = ctx.createLinearGradient(0, 0, 0, height);

  for (let i = 0; i <= EASE_STOPS; i++) {
    const t = i / EASE_STOPS;
    const alpha = easedDarken(t, curve);
    gradient.addColorStop(t, `rgba(0,0,0,${alpha})`);
  }

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
}

function ditherAlpha(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const image = ctx.getImageData(0, 0, width, height);
  const data = image.data;

  for (let i = 3; i < data.length; i += 4) {
    const noise = Math.random() - 0.5;
    const value = data[i] + noise * 2;

    if (value < 0) {
      data[i] = 0;
      continue;
    }

    if (value > 255) {
      data[i] = 255;
      continue;
    }

    data[i] = value;
  }

  ctx.putImageData(image, 0, 0);
}

/**
 * Reference height the gradient texture is rasterised at. The gradient is
 * parametric (t = 0…1 over the height) so a fixed reference is enough — we
 * scale the sprite vertically at draw time to fit the actual past area.
 *
 * Going through a fixed-size texture is critical: `height` changes every
 * single frame as the playhead moves, and re-rasterising the gradient
 * (canvas alloc + per-pixel dither + GPU upload) on each frame OOM'd the
 * WebView on iPad after ~30s of playback.
 */
const REFERENCE_HEIGHT = 256;

function createPastOverlayTexture(width: number, curve: PastOverlayCurve): Texture {
  const resolution = window.devicePixelRatio || 1;
  const canvas = document.createElement("canvas");
  const pixelWidth = Math.ceil(width * resolution);
  const pixelHeight = Math.ceil(REFERENCE_HEIGHT * resolution);
  canvas.width = pixelWidth;
  canvas.height = pixelHeight;
  const ctx = canvas.getContext("2d")!;
  ctx.scale(resolution, resolution);

  applyEasedGradient(ctx, width, REFERENCE_HEIGHT, curve);
  ditherAlpha(ctx, pixelWidth, pixelHeight);

  return Texture.from({ resource: canvas, resolution });
}

export class PastOverlay {
  private sprite: Sprite;
  private currentTexture?: Texture;
  private previousWidth = 0;

  constructor(stage: Container, private curve: PastOverlayCurve = SOFT_CURVE) {
    this.sprite = new Sprite();
    this.sprite.visible = false;
    stage.addChild(this.sprite);
  }

  /** Change la forme du voile ; la texture est refaite à la prochaine image. */
  setCurve(curve: PastOverlayCurve): void {
    if (curve === this.curve) {
      return;
    }

    this.curve = curve;
    this.previousWidth = 0;
  }

  draw(props: { width: number; height: number; y: number }): void {
    const { width, height, y } = props;

    if (height <= 0 || width <= 0) {
      this.sprite.visible = false;

      return;
    }

    if (width !== this.previousWidth) {
      this.previousWidth = width;
      this.currentTexture?.destroy(true);
      this.currentTexture = createPastOverlayTexture(width, this.curve);
      this.sprite.texture = this.currentTexture;
    }

    this.sprite.width = width;
    this.sprite.height = height;
    this.sprite.y = y;
    this.sprite.visible = true;
  }

  destroy(): void {
    this.currentTexture?.destroy(true);
    this.sprite.destroy();
  }
}
